import React from "react";
import { Link } from "react-router-dom";
import Emblem from "./Emblem";
import { openCookieSettings } from "./CookieConsent";
import { MapPin, Mail, Phone, Facebook, Instagram, Twitter, Youtube, MessageCircle, Music2 } from "lucide-react";
import { useLang } from "../i18n/LanguageContext";
import { useApi } from "../hooks/useApi";

const SOCIAL_ICONS = { facebook: Facebook, instagram: Instagram, twitter: Twitter, youtube: Youtube, whatsapp: MessageCircle, tiktok: Music2 };

export function SocialLinks({ className = "" }) {
  const { data } = useApi("/settings/social");
  const links = Object.entries(data || {}).filter(([, v]) => v);
  if (links.length === 0) return null;
  return (
    <div className={`flex flex-wrap gap-3 ${className}`} data-testid="social-links">
      {links.map(([k, url]) => { const Icon = SOCIAL_ICONS[k]; return (
        <a key={k} href={url} target="_blank" rel="noreferrer" aria-label={k} data-testid={`social-${k}`}
          className="flex h-9 w-9 items-center justify-center rounded-full border border-gold/40 text-gold transition-colors hover:bg-gold hover:text-crimson"><Icon size={16} /></a>
      ); })}
    </div>
  );
}

export default function Footer() {
  const { t } = useLang();
  const { data: contact } = useApi("/settings/contact");
  return (
    <footer className="mt-24 bg-slate-950 text-parchment/90" data-testid="footer">
      <div className="kente-border" />
      <div className="mx-auto grid max-w-7xl gap-10 px-6 py-14 md:grid-cols-4 lg:px-8">
        <div className="md:col-span-1">
          <div className="flex items-center gap-3">
            <Emblem size={52} />
            <div>
              <div className="font-serif-display text-xl font-bold text-gold">Okuapemman Association of Massachusetts</div>
              <div className="eyebrow text-gold/70">Est. 2010 · MA</div>
            </div>
          </div>
          <p className="mt-4 font-serif-display text-2xl italic text-gold">Ofie! Biakoye!</p>
          <p className="mt-1 text-sm text-parchment/60">{t("Home! Unity!")}</p>
          <SocialLinks className="mt-5" />
        </div>
        <div>
          <h4 className="eyebrow mb-4 text-gold">{t("Explore")}</h4>
          <ul className="space-y-2 text-sm">
            {[["/about","About Us"],["/about/leadership","Leadership"],["/membership","Membership"],["/events","Events"],["/welfare","Welfare"],["/contact/connect","Connect with us"]].map(([to,l])=>(
              <li key={to}><Link to={to} className="text-parchment/70 hover:text-gold">{t(l)}</Link></li>
            ))}
          </ul>
        </div>
        <div>
          <h4 className="eyebrow mb-4 text-gold">{t("Resources")}</h4>
          <ul className="space-y-2 text-sm">
            {[["/constitution","Constitution"],["/chapters","OKUSACA Chapters"],["/faq","FAQ"],["/registration","Apply to Join"],["/contact","Contact"]].map(([to,l])=>(
              <li key={to}><Link to={to} className="text-parchment/70 hover:text-gold">{t(l)}</Link></li>
            ))}
          </ul>
        </div>
        <div>
          <h4 className="eyebrow mb-4 text-gold">{t("Reach Us")}</h4>
          <ul className="space-y-3 text-sm text-parchment/70">
            <li className="flex items-center gap-2"><MapPin size={16} className="text-gold" /> <span data-testid="footer-address">{contact?.address ?? "Worcester, Massachusetts"}</span></li>
            <li className="flex items-center gap-2"><Mail size={16} className="text-gold" /> <a href={`mailto:${contact?.email ?? ""}`} data-testid="footer-email">{contact?.email ?? "info@okuapemman-ma.org"}</a></li>
            <li className="flex items-center gap-2"><Phone size={16} className="text-gold" /> <a href={`tel:${contact?.phone ?? ""}`} data-testid="footer-phone">{contact?.phone ?? "+1 (617) 555-0100"}</a></li>
          </ul>
        </div>
      </div>
      <div className="border-t border-white/10 py-5 text-center text-xs text-parchment/50">
        © {new Date().getFullYear()} Okuapemman Association of Massachusetts · {t("Affiliated with OKUSACA")} · {t("All rights reserved.")}
        <span className="mx-2">·</span><Link to="/privacy" className="hover:text-gold" data-testid="footer-privacy">{t("Privacy Policy")}</Link>
        <span className="mx-2">·</span><Link to="/terms" className="hover:text-gold" data-testid="footer-terms">{t("Terms of Use")}</Link>
        <span className="mx-2">·</span><Link to="/cookies" className="hover:text-gold" data-testid="footer-cookies">{t("Cookie Policy")}</Link>
        <span className="mx-2">·</span><button onClick={openCookieSettings} className="hover:text-gold" data-testid="footer-cookies">{t("Cookies")}</button>
      </div>
    </footer>
  );
}
